import { CSSProperties, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useCampaignBuilder, { getDefaultConversionEventName } from "Hooks/useCampaignBuilder";
import { clsx, formattedWebsiteUrl, makeKey, isSymphonyUrl, generateShortUniqueId } from "modules/Utils";
import CardSymphony from "elements/CardSymphony";
import InputSymphony from "elements/InputSymphony";
import useBrand, { BrandExistsState } from "Hooks/useBrand";
import ButtonSymphony from "elements/ButtonSymphony";
import { BrandContent, PresaveWebsiteStyle, DefaultPixelConversionEventNames } from "modules/Types";
import useWebsiteBuilder, { getWebsiteBuilderPath, WebsiteBuilderContentType } from "Hooks/useWebsiteBuilder";
import { CampaignType, PlaybookType, SYMPHONY_WEBSITE_URL, WEBSITE_TYPE_INFO, WebsiteType, WebsiteTemplateCategory } from "modules/Const";
import Banner from "elements/Banner";
import PresaveMiniature from "pages/MarketingPage/PreSave/Components/PresaveMiniature";
import Colors from "modules/Colors";
import DebugBox from "components/shareable/DebugBox";
import Loader from "elements/Loader";
import Icon from "elements/Icon";
import Sty from "modules/Sty";
import TagSymphony from "elements/TagSymphony";
import { useCampaign } from "Hooks/useCampaign";
import { extractSlugsFromFrontfacingLink } from "pages/MarketingPage/utils/links";
import { CreateNewWebsiteSteps } from "pages/WebsitePage/components/CreateNewWebsiteModal";

interface LinkSelectorCampaignViewProps {
    className?: string;
    style?: CSSProperties;
    title?: string;
    subtitle?: string;
    placeholder?: string;
}

const LinkSelectorCampaignView = ({
    className,
    style,
    title = "Link Selector",
    subtitle = "Enter the link you want to promote",
    placeholder = "https://www.instagram.com/yourusername"
}: LinkSelectorCampaignViewProps) => {
    const history = useHistory();
    const {
        linksToPromote,
        setLinksToPromote,
        playbookType,
        setConversionsEventName,
        setConversionsEnabled,
        nextCampaignStep
    } = useCampaignBuilder();
    const {
        currentBrand
    } = useBrand() as BrandExistsState

    const {
        getCampaigns,
        isLoadingCampaigns
    } = useCampaign();

    const {
        websites: websiteBuilderWebsites,
        getWebsites: getWebsiteBuilderWebsites,
        isLoadingWebsiteBuilder
    } = useWebsiteBuilder();

    const {
        setInitialWebsiteType,
        setShowCreateNewWebsiteModalV2,
        setSelectedTemplateCategory,
    } = useWebsiteBuilder();

    const [websitesFiltered, setWebsitesFiltered] = useState<BrandContent[]>([]);

    const openCreateNewWebsiteModal = (websiteKey?: string) => {
        setSelectedTemplateCategory(websiteKey as WebsiteTemplateCategory || null);
        setInitialWebsiteType(websiteKey as WebsiteType || null);
        // setShowCreateNewWebsiteModal(true);
        setShowCreateNewWebsiteModalV2(true);
    }

    const isWebsitesCampaign = playbookType === PlaybookType.META_EMAIL_SIGNUPS ||
        playbookType === PlaybookType.META_PRESAVES ||
        playbookType === PlaybookType.META_TICKET_SALES;

    const getWebsites_local = async () => {
        if (playbookType === PlaybookType.META_PRESAVES) {
            const campaignsLocal = await getCampaigns({ brandId: currentBrand?.id });
            const campaignsFiltered = campaignsLocal.filter(
                (campaign: any) => campaign.campaign_metadata.campaign_type === CampaignType.PRE_SAVE
            );

            setWebsitesFiltered(campaignsFiltered.map(
                (campaign: any) => {
                    const websiteContentMetadata = campaign.campaign_metadata.content || {};
                    const websiteUrl = campaign.campaign_metadata?.link;
                    const brandId = campaign.brand_id;
                    const campaignId = campaign.id;
                    const {
                        name,
                        slug,
                        thumbnail_url,
                    } = websiteContentMetadata;

                    const websiteLocal: BrandContent = {
                        id: campaignId,
                        content_metadata: {
                            thumbnail_url,
                            ...websiteContentMetadata
                        },
                        name,
                        slug,
                        type: WebsiteType.PRESAVE,
                        url: websiteUrl,
                        updatedAt: campaign.updatedAt || '',
                        createdAt: campaign.createdAt || '',
                        linked_campaigns: [],
                        prid: '',
                        brand_id: brandId
                    }

                    return websiteLocal;
                }
            ));
        } else {
            // Only use website builder websites
            const websiteBuilderWebsitesLocal = await getWebsiteBuilderWebsites({ brandId: currentBrand?.id });
            
            if (websiteBuilderWebsitesLocal) {
                switch (playbookType) {
                    case PlaybookType.META_TICKET_SALES:
                        // Filter for websites with tour content
                        const websitesWithTourContent = websiteBuilderWebsitesLocal.filter((website: any) => {
                            return website.websiteContent?.some((content: any) => content.type === WebsiteBuilderContentType.TOUR);
                        });
                        setWebsitesFiltered(websitesWithTourContent as any);
                        break;
                    case PlaybookType.META_EMAIL_SIGNUPS:
                        // Filter for websites with data collection content
                        const websitesWithDataCollection = websiteBuilderWebsitesLocal.filter((website: any) => {
                            return website.websiteContent?.some((content: any) => content.type === WebsiteBuilderContentType.COLLECT_DATA);
                        });
                        setWebsitesFiltered(websitesWithDataCollection as any);
                        break;
                    default:
                        setWebsitesFiltered([]);
                        break;
                }
            } else {
                setWebsitesFiltered([]);
            }
        }
    }

    useEffect(() => {
        if (isWebsitesCampaign) {
            getWebsites_local();
        }
    }, []);

    // useEffect(() => {
    //     if (websites) {
    //         const filteredWebsites = websites.filter(
    //             website => website.type === WEBSITE_TYPES.DATA_COLLECTOR
    //         );
    //         setDataCollectorWebsites(filteredWebsites);
    //     }
    // }, [websites]);

    // Check if the current URL is not a Symphony URL and clear conversion event if needed
    useEffect(() => {
        // Clear conversion events for non-Symphony URLs or empty URLs
        const currentUrl = linksToPromote.length > 0 ? linksToPromote[0]?.url : '';
        const isSymphonyUrlFlag = isSymphonyUrl(currentUrl);

        if (!isSymphonyUrlFlag) {
            setConversionsEventName('');
            setConversionsEnabled(false);
        }
    }, [linksToPromote, setConversionsEventName, setConversionsEnabled]);

    /** 
     * Validates if the provided string is a properly formatted URL
     * Checks for valid protocol, domain structure, and TLD
     */
    const isValidUrl = (url: string): boolean => {
        try {
            // Trim the URL and ensure it's not empty
            const trimmedUrl = url.trim();
            if (!trimmedUrl) {
                return false;
            }

            // Add protocol if missing for URL constructor
            const urlWithProtocol = trimmedUrl.match(/^https?:\/\//) ? trimmedUrl : `https://${trimmedUrl}`;

            // Let the URL constructor validate the URL
            const urlObj = new URL(urlWithProtocol);

            // Basic validation - ensure we have a hostname with at least one period
            return urlObj.hostname.includes('.') && urlObj.hostname.length > 3;
        } catch {
            return false;
        }
    }

    const setLinkToPromote = (url: string) => {
        const isValid = isValidUrl(url);

        // Only enable conversions if it's a Symphony URL
        const isSymphonyUrlFlag = isSymphonyUrl(url);

        if (!isSymphonyUrlFlag && url) {
            // For non-Symphony URLs, always clear the conversion event
            setConversionsEventName('');
            setConversionsEnabled(false);
        }

        // Only update if URL is empty or valid
        if (linksToPromote.length > 0) {
            let mainLink = { ...linksToPromote[0] };
            mainLink.url = url;
            mainLink.isValidUrl = isValid;
            setLinksToPromote([mainLink]);
        } else {
            setLinksToPromote([{
                url,
                name: 'Main Link',
                isValidUrl: isValid
            }]);
        }
    }

    const handleSelectWebsite = (website: any) => {
        let websiteUrl = '';

        if (playbookType === PlaybookType.META_PRESAVES) {
            // For presave campaigns, use the existing logic
            websiteUrl = website.url;
        } else {
            // For website builder websites, construct the URL
            websiteUrl = website.url || `${SYMPHONY_WEBSITE_URL}/${currentBrand?.slug}/${website.slug}`;
        }

        if (websiteUrl) {
            setLinkToPromote(websiteUrl);

            // Set conversion event based on playbook type
            if (playbookType) {
                const conversionEventName = getDefaultConversionEventName(playbookType);
                if(conversionEventName) {
                    setConversionsEventName(conversionEventName);
                }
                setConversionsEnabled(true);
            }

            nextCampaignStep();
        }
    }

    const handleCreateNewWebsite = () => {
        switch (playbookType) {
            case PlaybookType.META_EMAIL_SIGNUPS:
                const emailSignupSlug = generateShortUniqueId();
                const emailSignupPath = getWebsiteBuilderPath(currentBrand?.slug || '', emailSignupSlug);
                openCreateNewWebsiteModal(WebsiteTemplateCategory.COLLECT_DATA)
                history.push(`${emailSignupPath}`);
                break;
            case PlaybookType.META_PRESAVES:
                history.push('/marketing/new/presave');
                break;
            case PlaybookType.META_TICKET_SALES:
                const ticketSalesSlug = generateShortUniqueId();
                const ticketSalesPath = getWebsiteBuilderPath(currentBrand?.slug || '', ticketSalesSlug);
                openCreateNewWebsiteModal(WebsiteTemplateCategory.EVENTS)
                history.push(`${ticketSalesPath}`);
                break;
        }
    }

    const primaryLink = linksToPromote.length > 0 ? linksToPromote[0]?.url : '';
    const primaryLinkIsValid = linksToPromote.length > 0 ? linksToPromote[0]?.isValidUrl : false;

    const getWebsiteBannerTitle = () => {
        switch (playbookType) {
            case PlaybookType.META_EMAIL_SIGNUPS:
                return 'Boost Your Fan Data Collection';
            case PlaybookType.META_PRESAVES:
                return 'Build an active fanbase with Symphony Presave';
            case PlaybookType.META_TICKET_SALES:
                return 'Drive ticket sales with a custom fan landing page';
        }
    }

    const getWebsiteBannerSubtitle = () => {
        switch (playbookType) {
            case PlaybookType.META_EMAIL_SIGNUPS:
                return 'Create a signup page to collect fan emails directly and get detailed analytics on your audience engagement.';
            case PlaybookType.META_PRESAVES:
                return 'Create a Symphony Presave page to collect presaves, emails, and let fans opt-in to Forever Saves.';
            case PlaybookType.META_TICKET_SALES:
                return 'Create a ticket sales page to collect ticket sales and get detailed analytics on your audience engagement.';
        }
    }

    const getCreateWebsiteButtonText = () => {
        switch (playbookType) {
            case PlaybookType.META_EMAIL_SIGNUPS:
                return 'Create a Signup Page';
            case PlaybookType.META_PRESAVES:
                return 'Create a Presave';
            case PlaybookType.META_TICKET_SALES:
                return 'Create a Ticket Page';
        }
    }

    const renderWebsiteItem = (website: any, index: number) => {
        let websiteUrl = '';
        let thumbnailUrl = '';
        let websiteName = '';
        let websiteTypeTitle = '';
        let websiteStyle: PresaveWebsiteStyle = {
            bgType: 'artwork',
            primaryColor: Colors.purple,
            secondaryColor: Colors.white,
            buttonConfig: 'boxes',
            bgColor: Colors.white
        };

        if (playbookType === PlaybookType.META_PRESAVES) {
            // Presave website logic
            websiteName = website.name;
            websiteTypeTitle = WEBSITE_TYPE_INFO[website.type as WebsiteType]?.title;
            thumbnailUrl = website.content_metadata.thumbnail_url || website.content_metadata.website?.style?.thumbnail_url || website.content_metadata.preview_url;

            // Apply custom styling if available
            if (website.primary_customization) {
                Object.assign(websiteStyle, website.primary_customization);
            }

            // Construct URL for presaves
            if (website.type === WebsiteType.PRESAVE && website.url) {
                const linkSlugs = extractSlugsFromFrontfacingLink(website.url, currentBrand?.slug || '');
                websiteUrl = `${SYMPHONY_WEBSITE_URL}/${currentBrand?.slug}/${linkSlugs.urlSlug}`;
            } else {
                websiteUrl = `${SYMPHONY_WEBSITE_URL}/${currentBrand?.slug}/${website.slug}`;
            }
        } else {
            // Website builder website logic
            websiteUrl = `${SYMPHONY_WEBSITE_URL}/${currentBrand?.slug}/${website.slug}`;
            thumbnailUrl = website.style?.thumbnail_url || website.websiteContent?.find((c: any) => c.type === WebsiteBuilderContentType.IMAGE)?.imageContent?.image;
            websiteName = website.slug ? website.slug.charAt(0).toUpperCase() + website.slug.slice(1) : 'Website';

            // Get website type tag based on content
            if (website.websiteContent?.some((c: any) => c.type === WebsiteBuilderContentType.TOUR)) {
                websiteTypeTitle = 'Tour Site';
            } else if (website.websiteContent?.some((c: any) => c.type === WebsiteBuilderContentType.COLLECT_DATA)) {
                websiteTypeTitle = 'Data Collection';
            } else {
                websiteTypeTitle = 'Website';
            }

            // Apply website builder styling
            websiteStyle = {
                bgType: 'artwork',
                primaryColor: website.style?.textColor || Colors.purple,
                secondaryColor: website.style?.secondaryColor || Colors.white,
                buttonConfig: 'boxes',
                bgColor: website.style?.backgroundColor || Colors.white
            };
        }

        return (
            <div
                key={makeKey(website.id, index)}
                className="websiteItemContainer"
                style={sty.websiteItemContainer}
            >
                <PresaveMiniature
                    image={thumbnailUrl}
                    style={websiteStyle}
                    className="websiteImagePresave"
                />
                <div className="websiteDetails" style={sty.websiteDetails}>
                    <div className="websiteName" style={Sty.text16bold}>
                        {websiteName}
                    </div>
                    <TagSymphony
                        className="websiteBadge"
                        title={websiteTypeTitle}
                        type="square"
                    />
                    <div
                        className="websiteUrl"
                        style={sty.websiteUrl}
                    >
                        <div
                            className="websiteUrlText"
                            style={Sty.text14grey}
                        >
                            {websiteUrl}
                        </div>
                        <Icon
                            className="websiteUrlIcon"
                            icon="externalLink"
                            size={20}
                            onClick={() => window.open(websiteUrl, '_blank')}
                        />
                    </div>
                </div>
                <ButtonSymphony
                    className="selectWebsiteButton"
                    variant="text"
                    onClick={() => handleSelectWebsite(playbookType === PlaybookType.META_PRESAVES ? website : { 
                        ...website, 
                        url: websiteUrl,
                        type: playbookType === PlaybookType.META_TICKET_SALES ? WebsiteType.TOUR : WebsiteType.DATA_COLLECTOR
                    })}
                    iconRight="arrowRight"
                    iconRightSize={20}
                    iconRightOffset={-6}
                >
                    Select
                </ButtonSymphony>
            </div>
        );
    };

    const renderWebsitesList = () => {
        return (
            <div className="dataCollectorWebsitesList" style={sty.dataCollectorWebsitesList}>
                <div className="websitesListTitle">
                    Or use one of your existing sites:
                </div>
                {websitesFiltered.map((website: any, index) => renderWebsiteItem(website, index))}
            </div>
        );
    };

    const renderDataCollectorWebsites = () => {
        if (isLoadingCampaigns || isLoadingWebsiteBuilder) {
            return (
                <Loader
                    className="websiteLoadingLinkSelector"
                    title="Loading your sites..."
                />
            )
        }

        if (websitesFiltered.length === 0) {
            return (
                <Banner
                    className="websiteEmptyState"
                    color="maestro"
                    title={getWebsiteBannerTitle()}
                >
                    <div>
                        {getWebsiteBannerSubtitle()}
                    </div>
                    <div
                        className="analyticsHighlight"
                        style={sty.analyticsHighlight}
                    >
                        <Icon
                            className="analyticsIcon"
                            icon="analytics"
                            size={20}
                            color={Colors.purple}
                        />
                        <div style={Sty.text14purple}>
                            Track conversions, engagement, and audience insights in one place
                        </div>
                    </div>
                    <ButtonSymphony
                        className="createWebsiteButton"
                        style={{ marginTop: 16 }}
                        onClick={handleCreateNewWebsite}
                    >
                        {getCreateWebsiteButtonText()}
                    </ButtonSymphony>
                </Banner>
            );
        }

        return renderWebsitesList();
    };

    return (
        <CardSymphony
            className={clsx('linkToPromoteComponent', className)}
            style={style}
            title={title}
            subtitle={subtitle}
            size="small"
            isNoPadding
        >
            <InputSymphony
                className="linkToPromoteInput"
                label="Link to Promote"
                value={primaryLink}
                placeholder={placeholder}
                onChange={(value) => setLinkToPromote(value)}
            />
            {!primaryLinkIsValid && primaryLink &&
                <div className="linkToPromoteValidity">
                    Please enter a valid URL.
                </div>
            }

            {isWebsitesCampaign &&
                <div
                    className="websiteSelectSection"
                    style={sty.websiteSelectSection}
                >
                    {renderDataCollectorWebsites()}
                </div>
            }
        </CardSymphony>
    )
}

export default LinkSelectorCampaignView;

const sty: Record<string, CSSProperties> = {
    websiteSelectSection: {
        borderTop: Sty.border,
        marginTop: 8,
        paddingTop: 16
    },
    analyticsHighlight: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        marginTop: 8,
        padding: 8,
        backgroundColor: Colors.purpleTransparent2,
        borderRadius: 8,
        width: 'fit-content'
    },
    websiteItemContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        borderRadius: 16,
        border: '1px solid #EAEAEA',
        transition: 'all 0.2s ease',
        backgroundColor: Colors.white,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
    },
    websiteDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        flex: 1
    },
    websiteUrl: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer'
    },
    dataCollectorWebsitesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16
    }
};